import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withXhr } from '@angular/common/http';
import localePt from '@angular/common/locales/pt';
import { ApplicationConfig, ErrorHandler, LOCALE_ID, inject, isDevMode, provideAppInitializer } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideFuse } from '@fuse/fuse.provider';
import { ENVIRONMENT } from '@hmax-order/frontend/environment';
import { HmaxErrorHandler, initializeTranslocoLanguage, readStoredScheme } from '@hmax-order/frontend/utils';
import { TRANSLOCO_CONFIG, TranslocoConfig, TranslocoService, provideTransloco } from '@jsverse/transloco';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';
import { provideAuth } from './core/auth/auth.provider';
import { provideFirebase } from './core/auth/firebase.provider';
import { provideIcons } from './core/icons/icons.provider';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { MockApiService } from './mock-api';

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ErrorHandler, useClass: HmaxErrorHandler },
    { provide: LOCALE_ID, useValue: 'pt-br' },
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    provideAnimations(),
    provideHttpClient(withXhr()),
    provideRouter(appRoutes, withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),

    // Firebase (usado para o login opcional com conta Google)
    provideFirebase(),

    // Environment Provider
    {
      provide: ENVIRONMENT,
      useValue: environment,
    },

    // Material Date Adapter
    {
      provide: DateAdapter,
      useClass: LuxonDateAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'D',
        },
        display: {
          dateInput: 'DDD',
          monthYearLabel: 'LLL yyyy',
          dateA11yLabel: 'DD',
          monthYearA11yLabel: 'LLLL yyyy',
        },
      },
    },

    // Transloco Config
    provideTransloco({
      config: {
        availableLangs: [
          {
            id: 'pt',
            label: 'Português', // Portugues brasileiro
          },
          {
            id: 'en',
            label: 'English',
          },
          {
            id: 'es',
            label: 'Español',
          },
        ],
        defaultLang: 'pt',
        fallbackLang: 'en',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideAppInitializer(async () => {
      registerLocaleData(localePt);
      // ⚠️ Todo `inject()` tem de vir ANTES do primeiro `await`: o contexto de injeção termina no
      // await, e um `inject()` depois dele lança `NG0203` aqui dentro — o que trava o app no
      // splash, sem erro na tela, porque o bootstrap nunca conclui.
      const translocoService = inject(TranslocoService);
      const translocoConfig = inject(TRANSLOCO_CONFIG) as TranslocoConfig;

      await initializeTranslocoLanguage(translocoService, translocoConfig);
    }),

    // Fuse
    provideAuth(),
    provideIcons(),
    provideFuse({
      mockApi: {
        delay: 0,
        service: MockApiService,
      },
      fuse: {
        layout: 'classic',
        scheme: readStoredScheme() ?? 'light',
        screens: {
          sm: '600px',
          md: '960px',
          lg: '1280px',
          xl: '1440px',
        },
        theme: 'theme-brand',
        themes: [
          {
            id: 'theme-default',
            name: 'Default',
          },
          {
            id: 'theme-brand',
            name: 'Brand',
          },
          {
            id: 'theme-teal',
            name: 'Teal',
          },
          {
            id: 'theme-rose',
            name: 'Rose',
          },
          {
            id: 'theme-purple',
            name: 'Purple',
          },
          {
            id: 'theme-amber',
            name: 'Amber',
          },
        ],
      },
    }),
  ],
};
